import { getPosition } from "./geolocation";

// Single source of truth for attendance status colours, labels and codes —
// used by the muster grid, the month calendars and the legend so every view
// reads the same.

export const STATUS_META = {
  full: { label: "Present", code: "P", color: "#16a34a", soft: "#dcfce7" },
  half: { label: "Half day", code: "½", color: "#d97706", soft: "#fef3c7" },
  leave: { label: "Leave", code: "L", color: "#2563eb", soft: "#dbeafe" },
  absent: { label: "Absent", code: "A", color: "#dc2626", soft: "#fee2e2" },
  holiday: { label: "Holiday", code: "H", color: "#7c3aed", soft: "#ede9fe" },
  weekly_off: { label: "Week off", code: "WO", color: "#64748b", soft: "#f1f5f9" },
};

export const statusMeta = (status) => STATUS_META[status] || null;

// A live "today" cell for someone who hasn't checked in yet — shown instead of
// a premature red "Absent", since the working day isn't over. The day-end sweep
// decides the real status.
export const PENDING_META = { label: "Not in yet", code: "–", color: "#94a3b8", soft: "#f8fafc" };

// Short cell code; a 0.5-day leave shows as ½L so the register reads like a
// standard muster roll.
export const statusCode = (day) => {
  if (!day || !day.status) return "";
  if (day.status === "leave" && Number(day.leave_days) === 0.5) return "½L";
  return STATUS_META[day.status]?.code ?? "";
};

export const minutesLabel = (mins) => {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  if (m === 0) return "0m";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}m`;
  return r === 0 ? `${h}h` : `${h}h ${r.toString().padStart(2, "0")}m`;
};

// Best-effort device geolocation; resolves {} when unavailable or denied so a
// punch is never blocked — the slot just tags as "unknown". Shared by the My
// Attendance page and the top-bar widget.
//
// Deliberately coarse: the old version asked for high accuracy on a 4.5s
// budget, but a cold GPS fix takes 5-30s, so nearly every mobile punch timed
// out and recorded GEO_UNKNOWN. Wifi/cell positioning returns in about a
// second and is more than precise enough to note where someone punched in.
export async function captureLocation() {
  const fix = await getPosition({ highAccuracy: false, timeout: 8000, maximumAge: 120000 });
  return fix.ok ? { lat: fix.coords.latitude, lng: fix.coords.longitude } : {};
}

export const LEGEND = [
  ...Object.entries(STATUS_META).map(([status, meta]) => ({ status, ...meta })),
  { status: "pending", ...PENDING_META, label: "Not in yet (today)" },
  { status: "late", label: "Late", code: "•", color: "#f59e0b", soft: "transparent", marker: true },
  { status: "missing", label: "Missing checkout", code: "⚠", color: "#ef4444", soft: "transparent", marker: true },
];
